import type { Role } from '@develio/auth';
import { useCurrentOrg } from '@develio/auth/client';
import { LOCKED_FIELD_PATHS } from '@develio/shared';
import { type ReactNode, createContext, useContext, useMemo } from 'react';

/**
 * Context shared across all wizard steps to support ADR-029 input governance.
 *
 * The provider reads the current user's role from the auth context and
 * derives a `getIsLocked(path)` predicate consumed by {@link WizardField}.
 * Step components don't need to know about locking at all — they pass every
 * field through `WizardField`, which auto-renders read-only for locked paths
 * when the viewer is not an owner/consultant.
 *
 * The governance rules (ADR-029 §Role behavior):
 *   - owner     — edits everything; per-analysis divergence is an override.
 *   - consultant — edits everything; divergences require a signed waiver.
 *   - analyst/reviewer — locked fields are read-only.
 *
 * Until the active assumption/jurisdiction profiles finish loading, we don't
 * render values as "Set by {admin, date}" — we fall back to a neutral
 * "Locked by admin" badge.
 */

export interface ProfileCitation {
  /** Profile display name, e.g. "City of Austin" or "2026 Methodology". */
  readonly name: string;
  /** Display name of the admin who last edited the profile. */
  readonly lastEditedBy?: string;
  /** ISO timestamp. */
  readonly lastEditedAt?: string;
}

export interface WizardLockingValue {
  readonly role: Role | null;
  /** True when the viewer can edit locked methodology fields. */
  readonly canEditLocked: boolean;
  /** True iff the given dotted wizard path is governed AND the viewer can't edit it. */
  readonly isLocked: (path: string) => boolean;
  /** Profile info to display alongside locked fields. Undefined while loading. */
  readonly jurisdictionCitation?: ProfileCitation;
  readonly assumptionCitation?: ProfileCitation;
}

const WizardLockingContext = createContext<WizardLockingValue>({
  role: null,
  canEditLocked: true, // permissive default so non-provider callers aren't broken
  isLocked: () => false,
});

export interface WizardLockingProviderProps {
  readonly children: ReactNode;
  readonly jurisdictionCitation?: ProfileCitation;
  readonly assumptionCitation?: ProfileCitation;
}

export function WizardLockingProvider({
  children,
  jurisdictionCitation,
  assumptionCitation,
}: WizardLockingProviderProps) {
  const org = useCurrentOrg();
  const role = org?.role ?? null;

  // Owners and consultants can edit locked fields (consultants trigger a
  // waiver flow at submit time in Phase 4 / 7). Analysts and reviewers cannot.
  const canEditLocked = role === 'owner' || role === 'consultant';

  const value = useMemo<WizardLockingValue>(
    () => ({
      role,
      canEditLocked,
      isLocked: (path: string) => !canEditLocked && LOCKED_FIELD_PATHS.has(path),
      jurisdictionCitation,
      assumptionCitation,
    }),
    [role, canEditLocked, jurisdictionCitation, assumptionCitation],
  );

  return <WizardLockingContext.Provider value={value}>{children}</WizardLockingContext.Provider>;
}

export function useWizardLocking(): WizardLockingValue {
  return useContext(WizardLockingContext);
}
